// src/pages/PostSkillPage.js
// Post a Skill Share listing — any signed-in user, not just organisers.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { createSkillListing } from '../lib/db';
import { SKILL_TYPES, SKILL_CATEGORIES } from './SkillsPage';
import toast from 'react-hot-toast';

const PostSkillPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({
    type: 'offer_skill',
    category: 'other',
    title: '',
    description: '',
    contactEmail: user?.email || '',
    contactPhone: '',
    contactPrefs: ['call', 'text', 'email'],
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = 'Give it a short title';
    if (!form.contactEmail.trim() && !form.contactPhone.trim()) e.contact = 'Add an email or phone number so people can reach you';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) { toast.error('Please fill in all required fields'); return; }
    setSaving(true);
    try {
      const townId = localStorage.getItem('gather-town-id') || 'loughrea';
      await createSkillListing({
        type: form.type,
        category: form.category,
        title: form.title.trim(),
        description: form.description.trim(),
        contactEmail: form.contactEmail.trim() || null,
        contactPhone: form.contactPhone.trim() || null,
        contactPrefs: form.contactPrefs,
        townId,
        postedBy: user.uid,
        postedByName: user.displayName || 'A Gather member',
      });
      toast.success('Posted! Thanks for adding to the community.');
      navigate('/skills');
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong. Please try again.');
    }
    setSaving(false);
  };

  return (
    <div className="page" style={{ background: 'var(--cream)', padding: '52px 20px 40px' }}>
      <button onClick={() => navigate(-1)} style={s.backBtn}>← Back</button>
      <div style={s.h1}>Post to Skill Share</div>
      <div style={s.sub}>Offer something, or ask for help — whatever you need from your community.</div>

      <Field label="What kind of post is this?">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {Object.entries(SKILL_TYPES).map(([value, t]) => (
            <button key={value} type="button"
              style={{
                ...s.typeOption,
                border: form.type === value ? '2px solid var(--sage-deep)' : '1.5px solid #C8D9C9',
                background: form.type === value ? 'var(--sage-pale)' : 'var(--white)',
              }}
              onClick={() => set('type', value)}>
              {t.emoji} {t.label}
            </button>
          ))}
        </div>
      </Field>

      <Field label="Category">
        <select style={s.input} value={form.category} onChange={e => set('category', e.target.value)}>
          {SKILL_CATEGORIES.filter(c => c.value !== 'all').map(c => (
            <option key={c.value} value={c.value}>{c.emoji} {c.label}</option>
          ))}
        </select>
      </Field>

      <Field label="Title" error={errors.title}>
        <input
          style={{ ...s.input, borderColor: errors.title ? '#D97A6C' : '#C8D9C9' }}
          value={form.title}
          onChange={e => set('title', e.target.value)}
          placeholder="e.g. Happy to help with basic car maintenance"
        />
      </Field>

      <Field label="Details (optional)">
        <textarea
          style={{ ...s.input, height: 90, resize: 'vertical' }}
          value={form.description}
          onChange={e => set('description', e.target.value)}
          placeholder="Any details that help — availability, what you need in return, etc."
        />
      </Field>

      <Field label="Your email" error={errors.contact}>
        <input
          style={{ ...s.input, borderColor: errors.contact ? '#D97A6C' : '#C8D9C9' }}
          type="email"
          value={form.contactEmail}
          onChange={e => set('contactEmail', e.target.value)}
        />
      </Field>

      <Field label="Your phone (optional)">
        <input
          style={s.input}
          type="tel"
          value={form.contactPhone}
          onChange={e => set('contactPhone', e.target.value)}
        />
      </Field>

      <Field label="How would you like people to contact you?">
        <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 8 }}>
          All three are switched on by default — tap any one to turn it off.
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[
            ['call', '📞 Call'],
            ['text', '💬 Text'],
            ['email', '✉️ Email'],
          ].map(([key, label]) => {
            const checked = form.contactPrefs.includes(key);
            return (
              <button
                key={key}
                type="button"
                onClick={() => set('contactPrefs', checked ? form.contactPrefs.filter(p => p !== key) : [...form.contactPrefs, key])}
                style={{
                  flex: 1, padding: '10px 8px', borderRadius: 10, border: checked ? '2px solid var(--sage-deep)' : '2px solid transparent', cursor: 'pointer',
                  fontSize: 12.5, fontWeight: 700, fontFamily: 'Nunito',
                  background: checked ? 'var(--sage-deep)' : '#F0F0EE',
                  color: checked ? 'white' : 'var(--muted)',
                }}
              >
                {checked ? '✓ ' : ''}{label}
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 6 }}>
          Only the ones you provided details for above will ever actually show.
        </div>
      </Field>

      <button style={s.submitBtn} disabled={saving} onClick={handleSubmit}>
        {saving ? 'Posting...' : 'Post it'}
      </button>
    </div>
  );
};

const Field = ({ label, error, children }) => (
  <label style={{ display: 'block', marginBottom: 18 }}>
    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)', marginBottom: 6 }}>{label}</div>
    {children}
    {error && <div role="alert" style={{ fontSize: 12, color: '#D97A6C', marginTop: 4 }}>{error}</div>}
  </label>
);

const s = {
  backBtn: { background: 'none', border: 'none', color: 'var(--sage-dark)', fontSize: 14, fontWeight: 700, cursor: 'pointer', padding: 0, marginBottom: 18 },
  h1: { fontSize: 24, fontWeight: 800, color: 'var(--ink)', marginBottom: 4 },
  sub: { fontSize: 13.5, color: 'var(--muted)', fontWeight: 500, marginBottom: 24, lineHeight: 1.5 },
  input: {
    width: '100%', padding: '12px 14px', borderRadius: 12, border: '1.5px solid #C8D9C9',
    fontSize: 14, fontFamily: 'Nunito, sans-serif', boxSizing: 'border-box', background: 'var(--white)', color: 'var(--ink)',
  },
  typeOption: {
    padding: '12px 10px', borderRadius: 12, fontSize: 12.5, fontWeight: 700,
    cursor: 'pointer', fontFamily: 'Nunito, sans-serif', color: 'var(--ink)', textAlign: 'left',
  },
  submitBtn: {
    width: '100%', padding: '15px 16px', borderRadius: 14, marginTop: 8,
    background: 'var(--sage-deep)', color: 'white', border: 'none',
    fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'Nunito, sans-serif',
  },
};

export default PostSkillPage;
